import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Stethoscope, Spinner, ArrowsClockwise, UserGear, Tag,
  Clock, Percent, FirstAid, BookOpen, CheckCircle
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGENT_OPTIONS = [
  { value: "A",  label: "A — Superior",       color: "bg-green-100 text-green-800 border-green-300" },
  { value: "B",  label: "B — Good",           color: "bg-green-50 text-green-700 border-green-200" },
  { value: "C1", label: "C1 — Average",       color: "bg-yellow-100 text-yellow-800 border-yellow-300" },
  { value: "C2", label: "C2 — Below Average", color: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  { value: "D",  label: "D — Fraud",          color: "bg-red-100 text-red-800 border-red-300" },
];

const AGENT_DEDUCTIONS = { A: 0, B: 5, C1: 10, C2: 15, D: 25 };

export default function AdjudicationPanel({ claim, onUpdate }) {
  const [agentCategory, setAgentCategory] = useState(claim.agent_category || "");
  const [spotDiscount, setSpotDiscount] = useState(claim.spot_discount || 0);
  const [losOverride, setLosOverride] = useState(claim.los_override || "");
  const [saving, setSaving] = useState(false);
  const [showIcmr, setShowIcmr] = useState(false);

  const patient = claim.patient_info || {};
  const fa = claim.final_amount || {};
  const isProcessed = claim.status === "processed";

  useEffect(() => {
    setAgentCategory(claim.agent_category || "");
    setSpotDiscount(claim.spot_discount || 0);
    setLosOverride(claim.los_override || "");
  }, [claim.agent_category, claim.spot_discount, claim.los_override]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/claims/${claim.id}/adjudication`, {
        agent_category: agentCategory || null,
        spot_discount: spotDiscount,
        los_override: losOverride || null
      });
      toast.success("Adjudication updated and recalculated");
      onUpdate?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (!isProcessed) return null;

  const agentColor = AGENT_OPTIONS.find(a => a.value === agentCategory)?.color || "";
  const originalLos = patient.days_of_stay;
  const procConstruct = fa.procedure_construct;
  const icmr = claim.icmr_guideline;

  return (
    <div className="space-y-5" data-testid="adjudication-panel">
      {/* ── Claim Summary ── */}
      <section>
        <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Stethoscope size={18} className="text-[#002FA7]" />
          Clinical Summary
        </h3>
        <div className="border border-gray-200 rounded-md overflow-hidden">
          <div className="grid grid-cols-2 divide-x divide-gray-100">
            <div className="p-3">
              <p className="label-xs mb-1">Primary Diagnosis</p>
              <p className="text-sm font-medium text-gray-900">{patient.diagnosis_primary || "—"}</p>
            </div>
            <div className="p-3">
              <p className="label-xs mb-1">ICD Codes</p>
              <p className="text-sm font-medium text-gray-900">
                {patient.icd_codes && patient.icd_codes.length > 0
                  ? patient.icd_codes.join(", ")
                  : <span className="text-gray-400">Not extracted</span>}
              </p>
            </div>
          </div>
          <div className="border-t border-gray-100 p-3">
            <p className="label-xs mb-1">Procedures</p>
            {patient.procedures && patient.procedures.length > 0 ? (
              <div className="space-y-1">
                {patient.procedures.map((p, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="text-xs text-gray-400 font-mono mt-0.5">{i + 1}.</span>
                    <p className="text-sm text-gray-800">{typeof p === "string" ? p : p?.procedure_name || JSON.stringify(p)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No procedures extracted</p>
            )}
          </div>
          {/* Procedure Construct Match */}
          {procConstruct && (
            <div className="border-t border-blue-100 bg-blue-50/50 p-3">
              <p className="label-xs mb-1 text-blue-600">Procedure Construct Match</p>
              <div className="flex items-center gap-4 text-sm">
                <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300 capitalize">
                  {procConstruct.matched_procedure}
                </Badge>
                <span className="text-gray-600">Standard LOS: <strong>{procConstruct.standard_los} days</strong></span>
                <span className="text-gray-600">Cost range: <strong>INR {procConstruct.min_cost?.toLocaleString("en-IN")} – {procConstruct.max_cost?.toLocaleString("en-IN")}</strong></span>
              </div>
            </div>
          )}
          <div className="border-t border-gray-100 grid grid-cols-3 divide-x divide-gray-100">
            <div className="p-3">
              <p className="label-xs mb-0.5">Hospital</p>
              <p className="text-sm font-medium text-gray-900 truncate">{patient.hospital_name || "—"}</p>
            </div>
            <div className="p-3">
              <p className="label-xs mb-0.5">Doctor</p>
              <p className="text-sm font-medium text-gray-900 truncate">{patient.doctor_name || "—"}</p>
            </div>
            <div className="p-3">
              <p className="label-xs mb-0.5">LOS</p>
              <p className="text-sm font-medium text-gray-900">{patient.days_of_stay || "—"} days</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── ICMR Guidelines ── */}
      {icmr && (
        <section>
          <div className="border border-emerald-200 bg-emerald-50/50 rounded-md p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-emerald-900 flex items-center gap-2">
                <BookOpen size={16} className="text-emerald-600" />
                ICMR Guideline Applied: {icmr.procedure_name}
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowIcmr(true)}
                className="text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-100 gap-1"
                data-testid="view-icmr-btn"
              >
                <BookOpen size={12} /> View Full Guidelines
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-3 text-xs">
              <div>
                <p className="font-semibold text-emerald-700 uppercase tracking-wider mb-0.5">Standard LOS</p>
                <p className="text-sm font-bold text-gray-900">
                  {Object.entries(icmr.standard_los || {}).map(([k,v]) => `${v}d (${k})`).join(" / ")}
                </p>
              </div>
              <div>
                <p className="font-semibold text-emerald-700 uppercase tracking-wider mb-0.5">Max LOS</p>
                <p className="text-sm font-bold text-gray-900">{icmr.max_los} days</p>
              </div>
              <div>
                <p className="font-semibold text-emerald-700 uppercase tracking-wider mb-0.5">Room Category</p>
                <p className="text-sm font-bold text-gray-900">{icmr.room_category}</p>
              </div>
            </div>
          </div>

          {/* ICMR Modal */}
          <Dialog open={showIcmr} onOpenChange={setShowIcmr}>
            <DialogContent className="sm:max-w-[650px] max-h-[85vh] overflow-auto" data-testid="icmr-modal">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <BookOpen size={20} className="text-emerald-600" />
                  ICMR Guidelines: {icmr.procedure_name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {/* ICD Codes */}
                {icmr.icd_codes?.length > 0 && (
                  <div>
                    <p className="label-xs mb-1">ICD Codes</p>
                    <div className="flex flex-wrap gap-1.5">
                      {icmr.icd_codes.map((c, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{c}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* LOS */}
                <div>
                  <p className="label-xs mb-1">Standard Length of Stay</p>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(icmr.standard_los || {}).map(([k, v]) => (
                      <div key={k} className="border border-gray-200 rounded-md p-2 flex items-center justify-between">
                        <span className="text-xs text-gray-600 capitalize">{k.replace(/_/g, " ")}</span>
                        <span className="text-sm font-bold">{v} days</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Maximum allowed: <strong>{icmr.max_los} days</strong></p>
                </div>

                {/* Doctor Visits */}
                <div>
                  <p className="label-xs mb-1">Doctor Visits Allowed</p>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(icmr.doctor_visits || {}).map(([doc, count]) => (
                      <div key={doc} className="border border-gray-200 rounded-md p-2 flex items-center justify-between">
                        <span className="text-xs text-gray-600 capitalize">{doc.replace(/_/g, " ")}</span>
                        <span className="text-sm font-bold">{count} visits</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Investigations */}
                <div>
                  <p className="label-xs mb-2">Investigations Allowed</p>
                  {Object.entries(icmr.investigations || {}).map(([phase, tests]) => (
                    <div key={phase} className="mb-3">
                      <p className="text-xs font-semibold text-gray-700 capitalize mb-1">{phase.replace(/_/g, " ")}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {tests.map((t, i) => (
                          <Badge key={i} variant="outline" className="text-[10px] bg-gray-50 gap-0.5">
                            <CheckCircle size={10} className="text-green-500" /> {t}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Room & Notes */}
                <div>
                  <p className="label-xs mb-1">Room Category</p>
                  <p className="text-sm text-gray-800">{icmr.room_category}</p>
                </div>
                <div>
                  <p className="label-xs mb-1">Clinical Notes</p>
                  <p className="text-sm text-gray-600 bg-gray-50 rounded-md p-3 border border-gray-200">{icmr.notes}</p>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </section>
      )}

      {/* ── Adjudication Controls ── */}
      <section>
        <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <UserGear size={18} className="text-[#002FA7]" />
          Adjudication Controls
        </h3>
        <div className="border border-gray-200 rounded-md p-4 space-y-4 bg-gray-50/50">
          <div className="grid grid-cols-3 gap-4">
            {/* Agent Category */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5 flex items-center gap-1">
                <Tag size={12} /> Agent Category
              </Label>
              <Select value={agentCategory} onValueChange={setAgentCategory}>
                <SelectTrigger data-testid="agent-category-select" className={`h-9 ${agentColor}`}>
                  <SelectValue placeholder="Select agent type" />
                </SelectTrigger>
                <SelectContent>
                  {AGENT_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <span className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${
                          opt.value === "A" || opt.value === "B" ? "bg-green-500" :
                          opt.value === "C1" || opt.value === "C2" ? "bg-yellow-500" : "bg-red-500"
                        }`} />
                        {opt.label}
                        <span className="text-xs text-gray-400 ml-1">({AGENT_DEDUCTIONS[opt.value]}% ded)</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* LOS Override */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5 flex items-center gap-1">
                <Clock size={12} /> LOS Override (days)
              </Label>
              <Input
                type="number"
                min={0}
                value={losOverride}
                onChange={e => setLosOverride(e.target.value)}
                placeholder={originalLos ? `Original: ${originalLos}` : "—"}
                className="h-9"
                data-testid="los-override-input"
              />
            </div>

            {/* SPOT Discount */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5 flex items-center gap-1">
                <Percent size={12} /> SPOT Discount %
              </Label>
              <Input
                type="number"
                min={0}
                max={100}
                step={0.5}
                value={spotDiscount}
                onChange={e => setSpotDiscount(parseFloat(e.target.value) || 0)}
                placeholder="0"
                className="h-9"
                data-testid="spot-discount-input"
              />
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-gray-200">
            <div className="text-xs text-gray-400">
              {agentCategory && (
                <span>Agent deduction: <strong>{AGENT_DEDUCTIONS[agentCategory]}%</strong></span>
              )}
              {spotDiscount > 0 && (
                <span className="ml-3">SPOT: <strong>{spotDiscount}%</strong></span>
              )}
              {losOverride && (
                <span className="ml-3">LOS: <strong>{losOverride}d</strong> (was {originalLos || "?"}d)</span>
              )}
            </div>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="bg-[#002FA7] hover:bg-blue-800 text-white gap-1.5"
              data-testid="apply-adjudication-btn"
            >
              {saving ? <Spinner size={14} className="animate-spin" /> : <ArrowsClockwise size={14} />}
              Apply & Recalculate
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
